import React, { useState } from 'react';
import { HelpCircle, X, GraduationCap, ArrowRight, Clock } from 'lucide-react';
import type { AppView } from '../../types';
import type { TutorialKapitelId } from '../../tutorial/types';
import { kapitelFuerView } from '../../tutorial/register';

/**
 * LERNZENTRUM — das kontextsensitive „?" (Plan §2.1).
 *
 * Ein runder Knopf unten rechts. Ein Klick oeffnet eine schmale Seitenleiste
 * mit genau den Kapiteln, deren Tour in DIESER Ansicht startet
 * (`kapitelFuerView`), plus dem Weg ins Lernzentrum.
 *
 * Eigenstaendig und ohne Datenbank: die Liste kommt aus dem Register im
 * Bundle. Das „?" funktioniert deshalb auch dann, wenn die Migration noch
 * nicht gelaufen ist — es zeigt dann eben nur Titel und Dauer.
 *
 * Zwei Bauformen, eine Seitenleiste:
 *
 *  - `schwebend` (Vorgabe): der runde Knopf unten rechts, global aus
 *    `App.tsx` fuer alle Ansichten ohne eigenes „?".
 *  - `inline`: derselbe Knopf, aber ohne `fixed` — er sitzt dort im
 *    Textfluss, wo er eingebaut wird (Projektkopf, Reiterleiste der
 *    Verwaltung, Kopfzeile der Rechnungen). In diesen Ansichten blendet
 *    `App.tsx` das schwebende „?" aus; zwei Fragezeichen auf einem
 *    Bildschirm waeren eine Frage zuviel.
 *
 * Die Seitenleiste selbst ist in beiden Faellen dieselbe und liegt `fixed` am
 * rechten Rand — sie gehoert zur Ansicht, nicht zur Kopfzeile.
 *
 * ─── Ohne Kapitel kein Knopf ────────────────────────────────────────────────
 *
 * Gibt `kapitelFuerView` fuer diese Ansicht nichts her, rendert die Komponente
 * NICHTS. Das ist bewusst hier entschieden und nicht an den Einbaustellen:
 * v1 belegt rund 20 von ~75 Views, also ist „kein Kapitel" der Normalfall,
 * und ein Fragezeichen, das sich mit „Fuer diese Ansicht gibt es noch kein
 * Kapitel" meldet, ist ein gebrochenes Versprechen. Wer trotzdem ins
 * Lernzentrum will, findet es in der Seitenleiste.
 */

interface Props {
    /** Die gerade offene Ansicht — bestimmt, welche Kapitel angeboten werden. */
    view: AppView;
    /** Wechsel ins Lernzentrum; mit ID direkt auf das Kapitel. */
    onOeffneLernzentrum: (kapitelId?: TutorialKapitelId) => void;
    /** `inline` fuer den Einbau in eine Kopfleiste; Vorgabe ist der schwebende Knopf. */
    variant?: 'schwebend' | 'inline';
}

export const KontextHilfe: React.FC<Props> = ({ view, onOeffneLernzentrum, variant = 'schwebend' }) => {
    const [offen, setOffen] = useState(false);
    const kapitel = kapitelFuerView(view);

    const oeffne = (id?: TutorialKapitelId) => {
        setOffen(false);
        onOeffneLernzentrum(id);
    };

    // Nach den Hooks, damit die Reihenfolge stimmt.
    if (kapitel.length === 0) return null;

    const knopfKlasse = variant === 'inline'
        ? 'hidden md:flex w-9 h-9 shrink-0 items-center justify-center rounded-xl bg-white text-slate-400 hover:text-blue-600 border border-slate-200 shadow-sm transition-colors'
        : 'hidden md:flex fixed bottom-4 right-4 z-[115] w-10 h-10 items-center justify-center rounded-full bg-white text-slate-500 hover:text-blue-600 border border-slate-200 shadow-lg transition-colors';

    return (
        <>
            {/* ── Der Knopf ── */}
            <button
                type="button"
                onClick={() => setOffen((o) => !o)}
                aria-label="Hilfe zu dieser Ansicht"
                aria-expanded={offen}
                title="Hilfe zu dieser Ansicht"
                className={knopfKlasse}
            >
                {offen ? <X className="w-5 h-5" /> : <HelpCircle className="w-5 h-5" />}
            </button>

            {/* ── Die Seitenleiste ──
                Nur auf dem Desktop: mobil verdeckte sie den halben Bildschirm,
                und die mobile Hilfe ist im Plan (§2.4) ohnehin ein anderer
                Einstieg. */}
            {offen && (
                <>
                    <div className="hidden md:block fixed inset-0 z-[110] bg-black/20" onClick={() => setOffen(false)} />
                    <aside className="hidden md:flex fixed top-0 right-0 bottom-0 z-[114] w-[340px] bg-white border-l border-slate-200 shadow-2xl flex-col animate-slide-up">
                        <div className="shrink-0 px-4 py-3.5 border-b border-slate-200 flex items-start gap-2.5">
                            <div className="p-2 rounded-lg bg-blue-50 border border-blue-100 shrink-0">
                                <GraduationCap className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2 className="text-sm font-bold text-slate-800 leading-tight">Hilfe zu dieser Ansicht</h2>
                                <p className="text-[11px] text-slate-500">
                                    {kapitel.length === 1 ? '1 Kapitel passt hierher' : `${kapitel.length} Kapitel passen hierher`}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setOffen(false)}
                                aria-label="Hilfe schließen"
                                className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
                            {kapitel.map((k) => (
                                <button
                                    key={k.id}
                                    type="button"
                                    onClick={() => oeffne(k.id)}
                                    className="w-full text-left px-3 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
                                >
                                    <span className="block text-sm font-semibold text-slate-700">{k.titel}</span>
                                    <span className="block text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                                        <Clock className="w-3 h-3" />{k.dauerMin} Min.
                                    </span>
                                </button>
                            ))}
                        </div>

                        <div className="shrink-0 px-3 py-3 border-t border-slate-200">
                            <button
                                type="button"
                                onClick={() => oeffne()}
                                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3.5 py-2.5 rounded-xl transition-colors"
                            >
                                Zum Lernzentrum<ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </aside>
                </>
            )}
        </>
    );
};
